import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { ShareTypes, SHARE_TYPES } from '../../hooks';

import PlatformButton from '../PlatformButton';
import { Facebook as FacebookIcon, Twitter as TwitterIcon, Pinterest as PinterestIcon, Email as EmailIcon } from '../../icons';

import styles from './ShareModal.module.scss';
import Modal, { ModalProps } from '../Modal';

export type ShareModalProps = Omit<ModalProps, 'title' | 'btnClose' | 'close' | 'isVisible'> & {
  url: string;
};

export type OpenShareModalType = ShareModalProps & {
  resolve: (value: ShareTypes) => void;
  reject: (reason?: any) => void;
};

export type OpenShareModalListenerType = (args: OpenShareModalType) => void;

let listener: OpenShareModalListenerType | null = null;

export type OnCloseShareModalCallbackCallback = (type?: ShareTypes) => void;

const setListener = (callback: OpenShareModalListenerType) => {
  listener = callback;
  return removeListener;
};

const removeListener = () => {
  listener = null;
};

export const openShareModal = ({ cssClass, url }: ShareModalProps) =>
  new Promise<ShareTypes>((resolve, reject) => {
    listener &&
      listener({
        url,
        cssClass,
        resolve,
        reject,
      });
  });

const ShareModal: React.FC = () => {
  const [t] = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [cssClass, setCssClass] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [closeModal, setCloseModal] = useState<OnCloseShareModalCallbackCallback>(() => {});

  const onUnMount = setListener(async ({ url, cssClass, resolve }: OpenShareModalType) => {
    setIsVisible(true);
    setImageUrl(url);
    setCssClass(cssClass || null);
    const close = (shareType?: ShareTypes) => {
      setIsVisible(false);
    };
    setCloseModal(() => close);
  });

  useEffect(() => {
    return () => {
      onUnMount();
    };
  }, [onUnMount]);

  const onShareClick = (shareType: ShareTypes) => {
    closeModal(shareType);
  };

  return (
    <Modal title={t('SHARE')} btnClose={t('CLOSE')} isVisible={isVisible} cssClass={cssClass} close={closeModal}>
      <div data-testid='share-modal' className={styles.ff_share_modal__content}>
        {imageUrl && (
          <div className={styles.ff_share_modal__img__container}>
            <div className={classnames(styles.ff_share_modal__img)} style={{ backgroundImage: `url(${imageUrl})` }}></div>
          </div>
        )}
        <div className={styles.ff_share_modal__platform}>
          <PlatformButton onClick={() => onShareClick(SHARE_TYPES.facebook)}>
            <FacebookIcon />
          </PlatformButton>
          <PlatformButton onClick={() => onShareClick(SHARE_TYPES.pinterest)}>
            <PinterestIcon />
          </PlatformButton>
          <PlatformButton onClick={() => onShareClick(SHARE_TYPES.twitter)}>
            <TwitterIcon />
          </PlatformButton>
          <PlatformButton onClick={() => onShareClick(SHARE_TYPES.email)}>
            <EmailIcon />
          </PlatformButton>
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal;
